import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import promotionService from '../../services/marketingService';
import { Promotion, PromotionType, PromotionSchemeType, InvoicePage } from '../../types/marketingTypes';

const CampaignList: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [campaignToDelete, setCampaignToDelete] = useState<string | null>(null);
  const [availableInvoicePages, setAvailableInvoicePages] = useState<InvoicePage[]>([]);
  const navigate = useNavigate();

  // جلب قائمة الحملات وصفحات الفواتير
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // جلب الحملات وصفحات الفواتير بشكل متوازي
        const [campaignsResponse, invoicePagesResponse] = await Promise.all([
          promotionService.getAllPromotions(),
          promotionService.getAllInvoicePages()
        ]);
        
        setCampaigns(campaignsResponse || []);
        setAvailableInvoicePages(invoicePagesResponse || []);
      } catch (err) {
        console.error('Error fetching campaigns:', err);
        setError('حدث خطأ أثناء تحميل الحملات');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // تحويل نوع الحملة إلى نص مقروء
  const getPromotionTypeText = (type: PromotionType) => {
    switch (type) {
      case PromotionType.AUTOMATIC:
        return 'تلقائية';
      case PromotionType.COUPON:
        return 'كوبون';
      default:
        return 'غير معروف';
    }
  };

  // تحويل نوع آلية الحملة إلى نص مقروء
  const getSchemeTypeText = (schemeType: PromotionSchemeType) => {
    switch (schemeType) {
      case PromotionSchemeType.BUY_X_GET_Y:
        return 'اشتر X واحصل على Y';
      case PromotionSchemeType.PRODUCT_DISCOUNT:
        return 'خصم على المنتجات';
      case PromotionSchemeType.TAX_DISCOUNT:
        return 'خصم على الضريبة';
      case PromotionSchemeType.ORDER_DISCOUNT:
        return 'خصم على الطلب';
      case PromotionSchemeType.COMBO:
        return 'كومبو';
      case PromotionSchemeType.TARGET_PRICE:
        return 'سعر مستهدف';
      default:
        return 'غير معروف';
    }
  };

  // تحويل معرف صفحة الفاتورة إلى نص مقروء
  const getInvoicePageText = (pageId: string) => {
    const page = availableInvoicePages.find(p => p._id === pageId);
    return page ? page.name : 'صفحة غير معروفة';
  };

  // حذف حملة
  const handleDeleteCampaign = async () => {
    if (!campaignToDelete) return;

    try {
      await promotionService.deletePromotion(campaignToDelete);
      setCampaigns(campaigns.filter(campaign => campaign._id !== campaignToDelete));
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
    } catch (err) {
      console.error('Error deleting campaign:', err);
      setError('حدث خطأ أثناء حذف الحملة');
    }
  };

  // فتح مربع حوار الحذف
  const openDeleteDialog = (campaignId: string) => {
    setCampaignToDelete(campaignId);
    setDeleteDialogOpen(true);
  };

  // إغلاق مربع حوار الحذف
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCampaignToDelete(null);
  };

  // التوجه إلى صفحة إنشاء حملة جديدة
  const handleCreateCampaign = () => {
    navigate('/marketing/campaigns/new');
  };

  // التوجه إلى صفحة تعديل الحملة
  const handleEditCampaign = (campaignId: string) => {
    navigate(`/marketing/campaigns/${campaignId}/edit`);
  };

  // التوجه إلى صفحة عرض تفاصيل الحملة
  const handleViewCampaign = (campaignId: string) => {
    navigate(`/marketing/campaigns/${campaignId}/view`);
  };

  // تغيير الصفحة
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // تغيير عدد الصفوف في الصفحة
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // تنسيق التاريخ
  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('ar-SA');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" component="h2">
            الحملات التسويقية
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateCampaign}
          >
            إنشاء حملة جديدة
          </Button>
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="right">اسم الحملة</TableCell>
                <TableCell align="right">النوع</TableCell>
                <TableCell align="right">آلية الحملة</TableCell>
                <TableCell align="right">تاريخ البداية</TableCell>
                <TableCell align="right">تاريخ النهاية</TableCell>
                <TableCell align="right">صفحات الفواتير</TableCell>
                <TableCell align="right">الحالة</TableCell>
                <TableCell align="right">الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((campaign) => (
                  <TableRow key={campaign._id}>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {campaign.name}
                      </Typography>
                      {campaign.description && (
                        <Typography variant="caption" color="text.secondary">
                          {campaign.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={getPromotionTypeText(campaign.promotionType)}
                        size="small"
                        color={campaign.promotionType === PromotionType.COUPON ? 'secondary' : 'primary'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {getSchemeTypeText(campaign.schemeType)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatDate(campaign.startDate)}
                    </TableCell>
                    <TableCell align="right">
                      {formatDate(campaign.endDate)}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {campaign.applicableInvoicePages.map((pageId) => (
                          <Chip
                            key={pageId}
                            label={getInvoicePageText(pageId)}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={campaign.isActive ? 'نشطة' : 'غير نشطة'}
                        size="small"
                        color={campaign.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleViewCampaign(campaign._id)}
                        title="عرض"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditCampaign(campaign._id)}
                        title="تعديل"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openDeleteDialog(campaign._id)}
                        title="حذف"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={campaigns.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="عدد الصفوف في الصفحة:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
        />
      </Paper>

      {/* مربع حوار تأكيد الحذف */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          تأكيد حذف الحملة
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            هل أنت متأكد من أنك تريد حذف هذه الحملة؟ لا يمكن التراجع عن هذا الإجراء.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            إلغاء
          </Button>
          <Button onClick={handleDeleteCampaign} color="error" autoFocus>
            حذف
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignList;